// src/lib/two-factor-redirect.ts
//
// Client-side companion to the TWO_FACTOR_API_GATE check in secureRoute.ts.
//
// The gate is the authorisation control; this is only the UX that keeps a
// gated session from staring at a dashboard full of failed requests. When the
// server answers 403 { code: "TWO_FACTOR_REQUIRED" }, send the user to
// enrolment instead of surfacing seven error toasts.
//
// This wraps window.fetch once rather than editing each call site. 28 files go
// through apiFetch but 23 call fetch("/api/…") directly, and a rule that lives
// in one of those two places silently misses the other half — which is exactly
// how the first rollout of this gate produced 403s on the live dashboard.
// Patching the one function they all bottom out in cannot drift.

const TARGET = "/2fa/setup";

/**
 * Guard against re-entrancy: one redirect, even if 20 requests fail at once.
 *
 * Kept on `window` rather than in module scope because it describes *this
 * document* navigating away, and module state would outlive the window it
 * guards under any bundler that shares the module instance.
 */
type W = { __2faRedirectInstalled?: boolean; __2faRedirecting?: boolean };

function isTwoFactorRequired(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as Record<string, unknown>).code === "TWO_FACTOR_REQUIRED"
  );
}

export function installTwoFactorRedirect(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as W;
  if (w.__2faRedirectInstalled) return;
  w.__2faRedirectInstalled = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await original(input, init);

    // Cheap rejects first: only same-origin API 403s are candidates.
    if (res.status !== 403 || w.__2faRedirecting) return res;
    if (window.location.pathname.startsWith("/2fa")) return res;

    // Reading the body consumes it, so inspect a clone and hand the caller
    // back an untouched response.
    let body: unknown;
    try {
      body = await res.clone().json();
    } catch {
      return res;
    }
    if (!isTwoFactorRequired(body)) return res;

    // Re-check after the await. The guard above ran before the body read, so
    // concurrent 403s — a dashboard firing seven queries at once — would all
    // have passed it and each triggered a navigation. Nothing awaits between
    // this check and the assignment, so the pair is atomic.
    if (w.__2faRedirecting) return res;
    w.__2faRedirecting = true;
    // Hard navigation, not router.push: the gate applies to the whole app, so
    // a client-side transition would just re-fire the same failing queries.
    window.location.href = TARGET;
    return res;
  };
}
